import { Box, Flex, Text, Heading } from '@everlywell/ui-kit';
import React from 'react';

import BenefitCardDescriptionBullet, {
  BenefitCardDescriptionBulletProps,
} from './BenefitCardDescriptionBullet';

/**
 * Type definition for a part of the benefit card title
 * @typedef {Object} BenefitCardTitlePart
 * @property {string} [text] The text content of the title part
 * @property {string} [color] The color of the title part text
 */
export type BenefitCardTitlePart = {
  text?: string | null;
  color?: string | null;
};

/**
 * Props for the BenefitCardDescription component
 * @typedef {Object} BenefitCardDescriptionProps
 * @property {BenefitCardTitlePart} [topTitlePart] The top part of the title with text and color properties
 * @property {BenefitCardTitlePart} [bottomTitlePart] The bottom part of the title with text and color properties
 * @property {string | null} [backgroundColor] Background color for the card
 * @property {BenefitCardDescriptionBulletProps[]} [bullets] Array of bullet points to display
 */
export type BenefitCardDescriptionProps = {
  topTitlePart?: BenefitCardTitlePart;
  bottomTitlePart?: BenefitCardTitlePart;
  backgroundColor?: string | null;
  bullets?: BenefitCardDescriptionBulletProps[];
};

export const splitTextWithTrailingCommas = (text?: string): string[] => {
  if (!text) {
    return [];
  }

  const normalizedText = text.endsWith(',') ? text : `${text},`;

  // Match all groups ending with a comma and trim whitespaces
  return (normalizedText.match(/[^,]+,/g) || []).map((text) => text.trim());
};

/**
 * BenefitCardDescription component
 * A component that displays a card with a two-part title and a list of bullet points.
 * The title is split into top and bottom parts, with each part potentially having different colors.
 * Each title part can contain multiple comma-separated segments that are displayed on separate lines.
 * @param {BenefitCardTitlePart} [props.topTitlePart] The top part of the title with text and color properties
 * @param {BenefitCardTitlePart} [props.bottomTitlePart] The bottom part of the title with text and color properties
 * @param {string | null} [props.backgroundColor] Background color for the card
 * @param {BenefitCardDescriptionBulletProps[]} [props.bullets] Array of bullet points to display
 * @returns {JSX.Element} BenefitCardDescription component
 */
export default function BenefitCardDescription({
  topTitlePart,
  bottomTitlePart,
  backgroundColor,
  bullets,
}: BenefitCardDescriptionProps): JSX.Element {
  const topTopTitleTextSeparatedByCommas = splitTextWithTrailingCommas(
    topTitlePart?.text || '',
  );
  const bottomTitleTextSeparatedByCommas = splitTextWithTrailingCommas(
    bottomTitlePart?.text || '',
  );

  return (
    <Flex
      backgroundColor={backgroundColor || ''}
      width="100%"
      padding={[6, null, 8]}
      borderRadius="2xl"
      flexDirection="column"
      justifyContent="space-between"
      data-testid="benefit-card-description"
    >
      <Box marginBottom={[12, null, 8]}>
        <Heading
          size={['md', null, 'lg']}
          display="flex"
          flexDirection="column"
        >
          {topTopTitleTextSeparatedByCommas.map((text, index) => (
            <Text
              key={index}
              as="span"
              color={topTitlePart?.color || ''}
              fontSize="inherit"
              text-shadow=" 0px 4px 4px rgba(0, 0, 0, 0.25)"
            >
              {text}
            </Text>
          ))}
          {bottomTitleTextSeparatedByCommas.map((text, index) => (
            <Text
              key={index}
              as="span"
              color={bottomTitlePart?.color || ''}
              fontSize="inherit"
            >
              {/* Remove trailing comma from last item */}
              {bottomTitleTextSeparatedByCommas.length - 1 === index
                ? text.replace(/,$/, '')
                : text}
            </Text>
          ))}
        </Heading>
      </Box>
      {bullets && bullets.length > 0 && (
        <Flex flexDirection="column" gap={[2, null, 6]}>
          {bullets.map((bullet, index) => (
            <BenefitCardDescriptionBullet
              key={index}
              iconName={bullet.iconName}
              iconColor={bullet.iconColor}
              title={bullet.title}
              description={bullet.description}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
}
